/**
 * Une notification, rendue depuis son `type` et son `payload`.
 *
 * **Le serveur n'envoie aucun texte, et c'est délibéré.** Une phrase figée à
 * l'écriture resterait en français dans une interface passée à l'anglais — or
 * une notification se relit des semaines plus tard. i18next la compose à chaque
 * rendu, dans la langue courante.
 *
 * Le repli sur `types.unknown` n'est pas de la prudence décorative : le backend
 * peut gagner un type que ce bundle ne connaît pas encore — un onglet resté
 * ouvert pendant un déploiement suffit. Sans repli, i18next afficherait la clé
 * brute (`types.learning.certificate_issued.body`) dans la liste.
 */
import { useTranslation } from "react-i18next";

import type { Notification } from "@/features/notifications/api/useNotifications";
import { Button } from "@/shared/ui/Button";

interface NotificationItemProps {
  notification: Notification;
  onMarkRead?: (id: string) => void;
}

function asText(value: unknown): string {
  return typeof value === "string" ? value : "";
}

export function NotificationItem({ notification, onMarkRead }: NotificationItemProps) {
  const { t, i18n } = useTranslation("notifications");
  // Le bundle de traduction **est** la liste des types connus. Il y en avait
  // deux — un `Set` codé ici et les clés de `types.*` —, et deux listes finissent
  // par ne plus désigner les mêmes choses : `admin.warning` était traduit dans
  // les deux langues depuis la phase 11 et absent du `Set`, si bien qu'un
  // avertissement de modération s'affichait « cette notification ne peut pas
  // être affichée ». Rien n'échouait : le repli est précisément ce qui rendait
  // l'écart muet.
  const key = i18n.exists(`types.${notification.type}.title`, { ns: "notifications" })
    ? notification.type
    : "unknown";
  const payload = notification.payload as Record<string, unknown>;

  // `courseTitle` et non `{{count}}`-like : toute clé nommée `count` déclenche
  // la pluralisation d'i18next, qui chercherait alors `_one` / `_other` et ne
  // trouverait rien. La leçon vient de la phase 8 (`usedValue`, `limitValue`).
  const values = {
    courseTitle: asText(payload["course_title"]),
    planCode: asText(payload["plan_code"]),
    // Phase 10. `sender_name` vaut `null` pour un compte anonymisé entre
    // l'envoi et le traitement (docs/14 §5) : `asText` le ramène à la chaîne
    // vide plutôt que d'écrire « null » dans la phrase.
    senderName: asText(payload["sender_name"]),
    // Relecture de docs/16 §6. `promoted_name` vaut `null` pour un compte
    // devenu injoignable entre la promotion et l'acheminement.
    promotedName: asText(payload["promoted_name"]),
    // Phase 11 : le numéro de série, celui que le template invite à
    // communiquer pour faire vérifier l'attestation.
    serial: asText(payload["serial"]),
  };

  const isUnread = notification.read_at === null;
  const stamp = new Date(notification.created_at).toLocaleString(i18n.language);

  return (
    <li
      className={`flex items-start justify-between gap-4 border-b border-slate-200 p-4 last:border-0 dark:border-slate-700 ${
        isUnread ? "bg-sky-50/60 dark:bg-sky-950/30" : ""
      }`}
      data-testid="notification-item"
      data-unread={isUnread}
    >
      <div className="min-w-0">
        <p className="font-medium text-slate-900 dark:text-slate-100">{t(`types.${key}.title`)}</p>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
          {t(`types.${key}.body`, values)}
        </p>
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{stamp}</p>
      </div>
      {isUnread && onMarkRead !== undefined ? (
        <Button
          variant="ghost"
          onClick={() => {
            onMarkRead(notification.id);
          }}
          aria-label={t("list.markRead")}
        >
          {t("list.markRead")}
        </Button>
      ) : null}
    </li>
  );
}
